import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AIUsageBar from '../components/AIUsageBar';
import { CheckCircle2, Download, Printer, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuizGenerator() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [user, setUser] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [formData, setFormData] = useState({
    topic: '',
    grade: 'Year 4',
    subject: 'General',
    num_questions: 10
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      setCheckingAuth(false);
    } catch (error) {
      toast.error('Please sign in to use Quiz Generator');
      navigate('/login?redirect=/ai-tools/quiz-generator');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const generateQuiz = async (e) => {
    e.preventDefault();
    
    if (!formData.topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/ai/generate-quiz`,
        formData,
        { withCredentials: true }
      );
      
      setQuiz(response.data.content);
      toast.success('Quiz generated successfully!');
      
      // Refresh auth to update usage
      checkAuth();
    } catch (error) {
      console.error('Quiz generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate quiz');
    } finally {
      setLoading(false);
    }
  };

  const printQuiz = () => {
    window.print();
  };

  // Show loading while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-slate-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      <Navigation />
      
      <main className="flex-1 px-6 py-8 max-w-5xl mx-auto w-full">
        <div className="mb-8 no-print">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Quiz Generator</h1>
          <p className="text-slate-600">Create custom quizzes with answer keys</p>
          {user && (
            <AIUsageBar 
              className="mt-4"
              used={user.ai_generations_used || 0}
              limit={user.subscription_tier === 'free' ? 5 : user.subscription_tier === 'pro' ? 9999 : 200}
              tier={user.subscription_tier || 'free'}
            />
          )}
        </div>

        {!quiz && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
            <form onSubmit={generateQuiz} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Topic *
                </label>
                <input
                  type="text"
                  value={formData.topic}
                  onChange={(e) => setFormData({...formData, topic: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Fractions, The Water Cycle"
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Grade Level
                  </label>
                  <select
                    value={formData.grade}
                    onChange={(e) => setFormData({...formData, grade: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option>Year 1</option>
                    <option>Year 2</option>
                    <option>Year 3</option>
                    <option>Year 4</option>
                    <option>Year 5</option>
                    <option>Year 6</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Subject
                  </label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option>Maths</option>
                    <option>English</option>
                    <option>Science</option>
                    <option>General</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Number of Questions: {formData.num_questions}
                </label>
                <input
                  type="range"
                  min="5"
                  max="30"
                  value={formData.num_questions}
                  onChange={(e) => setFormData({...formData, num_questions: parseInt(e.target.value)})}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>5 questions</span>
                  <span>30 questions</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="generate-quiz-button"
                className="w-full bg-blue-600 text-white rounded-lg px-6 py-4 font-semibold shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating Quiz...
                  </>
                ) : (
                  <>Generate Quiz</>
                )}
              </button>
            </form>
          </div>
        )}

        {quiz && (
          <>
            <div className="no-print flex gap-3 mb-6">
              <button
                onClick={() => setQuiz(null)}
                className="px-6 py-3 bg-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-300"
              >
                Generate New Quiz
              </button>
              <button
                onClick={printQuiz}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 flex items-center gap-2"
              >
                <Printer className="w-5 h-5" />
                Print Quiz
              </button>
            </div>

            <div className="print-content bg-white rounded-xl shadow-sm border border-slate-200 p-12">
              {/* Header with branding */}
              <div className="text-center mb-8 border-b-2 border-slate-200 pb-6">
                <div className="mb-4">
                  <div className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-lg text-sm font-semibold">
                    LEARN WITH ZEENAT
                  </div>
                </div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">{quiz.title}</h1>
                <p className="text-slate-600 italic">Helping Teachers Save Time • Helping Every Child Learn.</p>
                <div className="mt-4 flex justify-center gap-8 text-sm text-slate-600">
                  <div><span className="font-semibold">Grade:</span> {quiz.grade}</div>
                  <div><span className="font-semibold">Subject:</span> {quiz.subject}</div>
                  <div><span className="font-semibold">Total Points:</span> {quiz.total_points}</div>
                  <div><span className="font-semibold">Time:</span> {quiz.time_limit}</div>
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-8">
                {quiz.questions && quiz.questions.map((q, idx) => (
                  <div key={idx} className="border-l-4 border-blue-500 pl-6">
                    <div className="flex justify-between items-start mb-3">
                      <p className="font-semibold text-slate-900 text-lg">
                        {q.question_number}. {q.question_text}
                      </p>
                      <span className="text-sm font-semibold text-blue-600 whitespace-nowrap ml-4">
                        {q.points} {q.points === 1 ? 'mark' : 'marks'}
                      </span>
                    </div>
                    
                    {q.options && q.options.length > 0 && (
                      <div className="ml-4 space-y-2">
                        {q.options.map((opt, oidx) => (
                          <div key={oidx} className="text-slate-700">{opt}</div>
                        ))}
                      </div>
                    )}
                    
                    <div className="mt-4 border-t border-dashed border-slate-300 pt-3 text-sm text-slate-500">
                      <p><strong>Answer:</strong> {q.correct_answer}</p>
                      {q.explanation && (
                        <p className="mt-1"><strong>Explanation:</strong> {q.explanation}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Scoring Guide */}
              <div className="mt-10 pt-6 border-t-2 border-slate-200">
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="font-bold text-slate-900 mb-3">Scoring Guide</h3>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-700"><span className="font-semibold">Total Points:</span> {quiz.total_points}</p>
                      <p className="text-slate-700"><span className="font-semibold">Passing Score:</span> {quiz.passing_score} ({Math.round((quiz.passing_score / quiz.total_points) * 100)}%)</p>
                    </div>
                    <div>
                      <p className="text-slate-700"><span className="font-semibold">Time Limit:</span> {quiz.time_limit}</p>
                      <p className="text-slate-700"><span className="font-semibold">Questions:</span> {quiz.questions?.length || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 pt-4 border-t border-slate-200 text-center text-xs text-slate-500">
                <p>Generated by Learn with Zeenat • www.learnwithzeenat.com</p>
              </div>
            </div>
          </>
        )}
      </main>

      <Footer />
      
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-content {
            box-shadow: none !important;
            border: none !important;
          }
        }
      `}</style>
    </div>
  );
}
